import { Injectable } from '@angular/core';
import { NonNullableFormBuilder } from '@angular/forms';
import { map, startWith } from 'rxjs';
import type { Step10FormInput, Step8FormInput, Step9FormInput } from '../../interfaces';
import { BaseForm } from '../base-form';
import { Step10Form } from './modules/step10-form';
import { Step8Form } from './modules/step8-form';
import { Step9Form } from './modules/step9-form';
import { jahreBeimVorversichererOptions } from './options/jahre-beim-vorversicherer-options';
import { kuendigungDurchOptions } from './options/kuendigung-durch-options';
import { schadenartOptions } from './options/schadenart-options';
import { selbstbeteiligungOptions } from './options/selbstbeteiligung-options';
import { versicherungssummeModusOptions } from './options/versicherungssumme-modus-options';
import { zusatzbausteinCards } from './options/zusatzbaustein-cards';

export interface VersicherungsdatenStepForms {
  step8: Step8FormInput;
  step9: Step9FormInput;
  step10: Step10FormInput;
}

export interface CoverageCard {
  title: string;
  description: string;
}

@Injectable({ providedIn: 'root' })
export class VersicherungsdatenFormService {
  readonly fixedCoverageCards: CoverageCard[] = [
    {
      title: 'Feuer',
      description: 'Brand, Blitzschlag und Explosion in deiner Wohnung',
    },
    {
      title: 'Leitungswasser',
      description: 'Schäden durch austretendes Leitungswasser in der Wohnung',
    },
    {
      title: 'Sturm & Hagel',
      description: 'Schäden durch Sturm ab Windstärke 8 und Hagel',
    },
  ];

  readonly zusatzbausteinCards = zusatzbausteinCards;

  readonly versicherungssummeModusOptions = versicherungssummeModusOptions;
  readonly selbstbeteiligungOptions = selbstbeteiligungOptions;
  readonly kuendigungDurchOptions = kuendigungDurchOptions;
  readonly jahreBeimVorversichererOptions = jahreBeimVorversichererOptions;
  readonly schadenartOptions = schadenartOptions;
}

export class VersicherungsdatenFormCreator extends BaseForm {
  constructor(
    private fb: NonNullableFormBuilder,
    private defaultValue?: VersicherungsdatenStepForms | null
  ) {
    super();
  }

  public step8Form = new Step8Form(this.fb, this.defaultValue?.step8);
  public step9Form = new Step9Form(this.fb, this.defaultValue?.step9);
  public step10Form = new Step10Form(this.fb, this.defaultValue?.step10);

  override stepsCtrls = {
    step8: this.step8Form.fg,
    step9: this.step9Form.fg,
    step10: this.step10Form.fg,
  };

  _fg = this.fb.group(this.stepsCtrls);
  fgDefaultValue = this._fg.getRawValue();

  fgValueChanges$ = this._fg.valueChanges.pipe(
    map(() => this._fg.getRawValue() as VersicherungsdatenStepForms)
  );

  fgValue$ = this._fg.valueChanges.pipe(
    startWith(null),
    map(() => this._fg.getRawValue() as VersicherungsdatenStepForms)
  );

  /** `setValue` cannot shrink step10's Vorschäden array, so that has to be emptied first. */
  reset(): void {
    this.step10Form.reset();
    this._fg.setValue(this.fgDefaultValue);
  }
}
